import { useEffect, useMemo, useState } from 'react';
import { IAddress } from '@les-sherpas/sherpas-toolbox';
import { useRouter } from 'next/router';

import useReplaceWindowURL from '@/components/Search/hooks/useReplaceWindowURL';
import {
  SearchDefaultProps,
  SearchFiltersType,
  SidePanelFilters,
} from '@/components/Search/types';
import { getInitialState } from '@/components/Search/utils';
import useDebounce from '@/hooks/debounce';
import googleGeocoderService from '@/Organisms/SearchBar/Input/GeocoderPlace.service';

type UseSearchFiltersType = (_: SearchDefaultProps) => SearchFiltersType;

const useSearchFilters: UseSearchFiltersType = ({
  defaultSubject,
  defaultAddress,
}) => {
  const { query } = useRouter();
  const { replaceWindowUrl } = useReplaceWindowURL();
  const initialState = useMemo(() => getInitialState(query), [query]);

  const [subject, setSubject] = useState(defaultSubject);
  const [address, setAddress] = useState<IAddress>();
  const [sidePanelFilters, setSidePanelFilters] =
    useState<SidePanelFilters>(initialState);

  const debounceSidePanelFilters = useDebounce(sidePanelFilters, 500);
  const debounceSubject = useDebounce(subject, 500);
  const debounceAddress = useDebounce(address, 500);

  const debouncedMinPrice = debounceSidePanelFilters.prices[0];
  const debouncedMaxPrice = debounceSidePanelFilters.prices[1];

  useEffect(() => {
    replaceWindowUrl({
      min: debouncedMinPrice,
      max: debouncedMaxPrice,
    }).then();
  }, [debouncedMinPrice, debouncedMaxPrice]);

  useEffect(() => {
    // TODO: @devdee rethink this part
    if (defaultAddress) {
      (async () => {
        setAddress(await googleGeocoderService(defaultAddress));
      })();
    }
  }, [defaultAddress]);

  useEffect(() => {
    replaceWindowUrl({ address: address?.formattedAddress ?? null }).then();
  }, [address?.formattedAddress]);

  useEffect(() => {
    const {
      educationalStages,
      courseLocation,
      availabilities,
      gender,
      onlyAnswerFastSherpas,
      onlyCertifiedSherpas,
      onlyAvailableSherpas,
    } = debounceSidePanelFilters;

    replaceWindowUrl({
      educationalStages,
      courseLocation,
      availabilities,
      gender,
      onlyAnswerFastSherpas,
      onlyCertifiedSherpas,
      onlyAvailableSherpas,
    }).then();
  }, [debounceSidePanelFilters]);

  useEffect(() => {
    replaceWindowUrl({ subject: debounceSubject }).then();
  }, [debounceSubject]);

  return {
    subject,
    address,
    sidePanelFilters,
    setSubject,
    setAddress,
    setSidePanelFilters,
    debounce: {
      minPrice: debouncedMinPrice,
      maxPrice: debouncedMaxPrice,
      subject: debounceSubject,
      address: debounceAddress,
      sidePanelFilters: debounceSidePanelFilters,
    },
  };
};

export default useSearchFilters;
